<template>
	<div class="item-param-container">
		<div v-for="(id, index) in Object.keys(itemParamsMap)" :key="id">
			<div class="mx-2 mt-2 display-flex flex-justify-space-between">
				<div class="text-[12px]">{{ itemName(index) }}</div>
				<RoundButton v-if="canDeleteItem" @click="deleteItem(id, index)">
					<AppIcon name="trash" class="si"></AppIcon>
				</RoundButton>
			</div>
			<div class="item-param display-flex flex-column mb-4">
				<ParamDispatcherDataProvider
					v-for="nestedParam in visibleNestedParams"
					class="item-param-dispatcher"
					:param="{ ...nestedParam, multiItemId: `${param.key}.${id}` }"
					:node="itemNode(id)"
					:key="buildFieldKey(nestedParam, id)"
					:disabled="getIsDisabled(nestedParam, id)"
					:workflowVersion="workflowVersion"
					@update-input-field="(payload) => updateInputField(payload, id)"
					@update-input-field-label="(payload) => updateInputFieldLabel(payload, id)"
					@update-input-fallback="(payload) => updateInputFallback(payload, id)"
					v-bind="$attrs"
				/>
				<ParamDispatcherDataProvider
					v-for="nestedParam in dynamicNestedParams(id)"
					class="item-param-dispatcher"
					:param="{ ...nestedParam, multiItemId: `${param.key}.${id}` }"
					:node="itemNode(id)"
					:key="buildFieldKey(nestedParam, id)"
					:workflowVersion="workflowVersion"
					:disabled="getIsDisabled(nestedParam, id)"
					@update-input-field="(payload) => updateInputField(payload, id)"
					@update-input-field-label="(payload) => updateInputFieldLabel(payload, id)"
					@update-input-fallback="(payload) => updateInputFallback(payload, id)"
					v-bind="$attrs"
				/>
			</div>
		</div>
		<DashedButton v-if="!reachedItemLimit" @click="addItem" class="mt-2 width-100"> + Add {{ paramNoun }} </DashedButton>
	</div>
</template>
<script>
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuid } from 'uuid';

import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const.js';
import { editNodeParams, editNodeOriginal } from '@/store/modules/selectedNode/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import ParamsService from '@/utils/ParamsService';

export default {
	name: 'MultipleItemPropertyInput',
	components: {
		ParamDispatcherDataProvider: () => import('@/components/BlockEditor/BlockEditorParams/ParamDispatcherDataProvider'),
	},
	props: {
		param: { type: Object },
		node: { type: Object },
		workflowVersion: { type: Object },
		value: { type: [String, Number, Boolean, Array, Object] },
	},
	methods: {
		setInitialState() {
			if (!this.localField.params_map) {
				this.$set(this.localField, 'params_map', {});
			}
			if (!this.localField.params_map[this.param.key]) {
				this.$set(this.localField.params_map, this.param.key, cloneDeep(this.defaultItem));
			}

			if (!this.localField.params_map_labels) {
				this.$set(this.localField, 'params_map_labels', {});
			}
			if (!this.localField.params_map_labels?.[this.param.key]) {
				this.$set(this.localField.params_map_labels, this.param.key, cloneDeep(this.defaultItem));
			}

			if (!this.localField.fallbacks) {
				this.$set(this.localField, 'fallbacks', {});
			}
			if (!this.localField.fallbacks[this.param.key]) {
				this.$set(this.localField.fallbacks, this.param.key, cloneDeep(this.defaultItem));
			}
		},
		updateInputField(payload, itemId) {
			const { map, preventUpdateEditNode, syncEditNodeAfterUpdate } = payload;
			this.$set(this.innerItemParamsMap(itemId), map.key, map.value);
			const mapItem = { key: this.param.key, value: this.itemParamsMap };
			const body = { map: mapItem, param: this.param, preventUpdateEditNode, syncEditNodeAfterUpdate };
			this.$emit('update-input-field', body);
		},
		updateInputFieldLabel(payload, itemId) {
			const { label, preventUpdateEditNode } = payload;
			this.$set(this.innerItemParamsMapLabels(itemId), label.key, label.value);
			this.$emit('update-input-field-label', {
				param: this.param,
				label: { key: this.param.key, value: this.itemParamsMapLabels },
				preventUpdateEditNode,
			});
		},
		updateInputFallback(payload, itemId) {
			const { key, value } = payload;
			this.$set(this.innerItemFallbacks(itemId), key, value);
			this.$emit('update-input-fallback', { key: this.param.key, value: this.itemFallbacks });
		},
		innerItemParamsMap(itemId) {
			if (!this.itemParamsMap[itemId]) {
				this.$set(this.localField.params_map[this.param.key], itemId, {});
			}
			return this.itemParamsMap[itemId];
		},
		innerItemParamsMapLabels(itemId) {
			if (!this.itemParamsMapLabels[itemId]) {
				this.$set(this.localField.params_map_labels[this.param.key], itemId, {});
			}
			return this.itemParamsMapLabels?.[itemId];
		},
		innerItemFallbacks(itemId) {
			if (!this.itemFallbacks[itemId]) {
				this.$set(this.localField.fallbacks[this.param.key], itemId, {});
			}
			return this.itemFallbacks[itemId];
		},
		itemNode(itemId) {
			return {
				...this.localField,
				params_map: this.innerItemParamsMap(itemId),
				params_map_labels: this.innerItemParamsMapLabels(itemId),
				fallbacks: this.innerItemFallbacks(itemId),
			};
		},
		itemName(index) {
			return `${this.paramNoun} ${index + 1}`;
		},
		async deleteItem(id, index) {
			if (!this.canDeleteItem) {
				return;
			}
			const res = await AppDialogManagerService.openConfirmationDialog({
				title: `Are you sure you want to delete ${this.itemName(index)}?`,
				yesText: 'Delete',
				noText: 'Cancel',
			});
			if (res === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
				this.$delete(this.itemParamsMap, id);
				this.$delete(this.itemParamsMapLabels, id);
				this.$delete(this.itemFallbacks, id);
				this.$emit('update-input-field', {
					map: { key: this.param.key, value: this.itemParamsMap },
					param: this.param,
				});
				this.$emit('update-input-field-label', {
					param: this.param,
					label: { key: this.param.key, value: this.itemParamsMapLabels },
				});
				this.$emit('update-input-fallback', { key: this.param.key, value: this.itemFallbacks });
			}
		},
		addItem() {
			if (this.reachedItemLimit) {
				return;
			}
			this.$set(this.localField?.params_map?.[this.param.key], uuid(), {});
		},
		dynamicNestedParams(itemId) {
			const dynamicParams = this.editNodeParams?.find((p) => p.key === this.param.key)?.params || [];
			return dynamicParams.find((p) => p.key === '__internal__multiple__item__')?.[itemId] || [];
		},
		buildFieldKey(nestedParam, itemId) {
			return ParamsService.buildFieldKey(nestedParam, this.innerItemParamsMapOriginalNode(itemId));
		},
		getIsDisabled(nestedParam, itemId) {
			return ParamsService.getIsDisabled(nestedParam, this.innerItemParamsMapOriginalNode(itemId));
		},
		innerItemParamsMapOriginalNode(itemId) {
			return this.editNodeOriginal?.params_map?.[this.param.key]?.[itemId] || {};
		},
	},
	computed: {
		...mapGettersShorthand({
			editNodeParams,
			editNodeOriginal,
		}),
		visibleNestedParams() {
			return this.param.params.filter((p) => !p.hidden && !['$func$', '<F>'].includes(p));
		},
		itemParamsMap() {
			return this.localField.params_map[this.param.key];
		},
		itemParamsMapLabels() {
			return this.localField.params_map_labels[this.param.key];
		},
		itemFallbacks() {
			return this.localField.fallbacks[this.param.key];
		},
		paramNoun() {
			return (this.param.noun || this.param.name).toLowerCase();
		},
		reachedItemLimit() {
			return this.param.max_items && Object.keys(this.itemParamsMap).length >= this.param.max_items;
		},
		canDeleteItem() {
			return !this.param.required || Object.keys(this.itemParamsMap).length > 1;
		},
	},
	created() {
		this.setInitialState();
	},
	data() {
		return {
			defaultItem: { [uuid()]: {} },
			localField: cloneDeep(this.node),
		};
	},
};
</script>
<style lang="scss" scoped>
.item-param-container {
	border: 1px solid var(--seconday-border-color);
	border-radius: 5px;
	padding: 10px;
	margin-top: 10px;
}
.item-param {
	border: 1px solid var(--seconday-border-color);
	border-radius: 5px;
	padding: 10px;
	margin-top: 10px;
}
</style>
